// What the employment-history page needs to know about time, on top of the
// generic month arithmetic in src/utils/date/monthPeriods.
//
// Only two things here are about employment: an entry is a record with a start
// and an optional end, and whether the driver may continue is the job listing's
// decision, not this page's.

import {
  analyzeMonthCoverage,
  toMonthIndex,
  trailingMonthWindow,
} from 'src/utils/date/monthPeriods'
import type {
  MonthCoverage,
  OpenMonthPeriod,
} from 'src/utils/date/monthPeriods'

export { isEmploymentHistoryCovered } from '../../utils/employmentHistoryCoverage'

/**
 * How much history the page draws when the job listing hasn't asked for a
 * minimum. Nothing is enforced in that case — the bar is the whole intervention.
 */
export const DEFAULT_HISTORY_YEARS = 10

export interface CoverageEntry {
  startDate?: Date | string | null
  /** Null or undefined means "still there" — the entry runs to the present. */
  endDate?: Date | string | null
}

/** Entries a driver has filled in, as periods. Entries with no start are ignored. */
export const toMonthPeriods = (entries: CoverageEntry[]): OpenMonthPeriod[] => {
  const periods: OpenMonthPeriod[] = []
  for (const entry of entries ?? []) {
    const start = toMonthIndex(entry.startDate)
    if (start === null) continue
    periods.push({ start, end: toMonthIndex(entry.endDate) })
  }
  return periods
}

/**
 * Everything the page draws itself from: which periods are unaccounted for, how
 * much is covered, and the window those answers refer to.
 *
 * The window here is exactly `years` years — 120 months for ten — and the
 * submission gate (`isEmploymentHistoryCovered`) checks the same window, as
 * does the API. What the bar calls complete is what the server accepts.
 */
export const analyzeCoverage = (
  entries: CoverageEntry[],
  years: number | null | undefined,
  now: Date = new Date()
): MonthCoverage =>
  analyzeMonthCoverage(
    toMonthPeriods(entries),
    trailingMonthWindow(years && years > 0 ? years : DEFAULT_HISTORY_YEARS, now)
  )
